/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import { UserCheck, Users, TrendingUp, DollarSign, Loader2, BarChart3, FileText, Briefcase } from "lucide-react";
import { DailyReportsView } from "./realestate/DailyReportsView";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function TeamLeadPortalPage() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/t/re/team-lead/me");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 404) toast.error("You are not registered as a team lead");
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-12 flex items-center justify-center text-black/45"><Loader2 className="animate-spin" /></div>;
  const { team_lead, marketers, sales, leads, daily_reports, team_stats } = data;

  return (
    <div className="p-6 lg:p-8" data-testid="team-lead-portal">
      <div className="relative overflow-hidden rounded-3xl mb-6 bg-gradient-to-br from-violet-900 via-fuchsia-700 to-pink-600 text-white p-7">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-pink-500/20 blur-3xl" />
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-xl border border-white/20 flex items-center justify-center font-display font-extrabold text-3xl">
              {(team_lead.name || "T")[0].toUpperCase()}
            </div>
            <div>
              <div className="text-xs uppercase font-bold tracking-[0.18em] text-white/70">Team Lead</div>
              <h1 className="font-display font-extrabold text-3xl mt-1">{team_lead.name}</h1>
              <div className="text-sm text-white/70 mt-1 flex items-center gap-2"><Briefcase size={12} /> {team_lead.agency_name}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Mini lbl="Marketers" v={team_stats.marketers_total} />
            <Mini lbl="Sales" v={team_stats.sales_count} />
            <Mini lbl="Revenue" v={ngn(team_stats.revenue)} />
            <Mini lbl="Leads" v={team_stats.leads_total} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-black/8 overflow-x-auto">
        {[
          { k: "overview", lbl: "Overview", icon: BarChart3 },
          { k: "marketers", lbl: "My Team", icon: Users },
          { k: "reports", lbl: "Daily Reports", icon: FileText },
        ].map((t) => {
          const Icon = t.icon, active = tab === t.k;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tl-tab-${t.k}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 whitespace-nowrap ${active ? "border-violet-600 text-violet-700" : "border-transparent text-black/55 hover:text-black"}`}>
              <Icon size={14} /> {t.lbl}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div>
          <Section title="Recent sales">
            {sales.length === 0 ? <Empty msg="No sales yet" /> : (
              <table className="w-full text-sm">
                <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
                  <tr><Th>Code</Th><Th>Property</Th><Th>Customer</Th><Th right>Price</Th><Th>Stage</Th></tr>
                </thead>
                <tbody>
                  {sales.slice(0, 15).map((s) => (
                    <tr key={s.id} className="border-t border-black/5">
                      <Td className="font-mono text-xs">{s.code}</Td>
                      <Td>{s.property_title}</Td>
                      <Td>{s.customer_name}</Td>
                      <Td right className="font-bold">{ngn(s.sale_price)}</Td>
                      <Td><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold capitalize">{s.stage}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
          <Section title="Lead funnel">
            {leads.length === 0 ? <Empty msg="No team leads yet" /> : (
              <div className="grid grid-cols-7 gap-2 p-4">
                {["new", "contacted", "interested", "inspection", "negotiation", "closed_won", "closed_lost"].map((stage) => {
                  const count = leads.filter((l) => l.stage === stage).length;
                  return (
                    <div key={stage} className="bg-[#FAFBFD] rounded-xl p-3 text-center">
                      <div className="text-xs uppercase font-bold text-black/55 capitalize">{stage.replace("_", " ")}</div>
                      <div className="font-display font-extrabold text-2xl text-black mt-1">{count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>
      )}

      {tab === "marketers" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {marketers.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-black/8 p-5" data-testid={`tl-marketer-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold text-lg">{(m.name || "?")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-base text-black truncate">{m.name}</div>
                  <div className="text-xs text-black/55">{m.referral_code}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div><div className="text-[10px] uppercase text-black/55 font-bold">Sales</div><div className="font-display font-extrabold">{m.sales_count}</div></div>
                <div><div className="text-[10px] uppercase text-black/55 font-bold">Wallet</div><div className="font-display font-extrabold text-emerald-700 text-sm">{ngn(m.wallet_balance)}</div></div>
                <div><div className="text-[10px] uppercase text-black/55 font-bold">Status</div><div className={`text-[10px] font-bold uppercase ${m.status === "active" ? "text-emerald-700" : "text-red-700"}`}>{m.status}</div></div>
              </div>
            </div>
          ))}
          {marketers.length === 0 && <Empty msg="No marketers under your supervision" />}
        </div>
      )}

      {tab === "reports" && (
        <div data-testid="tl-reports-tab">
          <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
            {daily_reports.length === 0 ? <Empty msg="No daily reports from your team yet" /> : (
              <table className="w-full text-sm">
                <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
                  <tr><Th>Date</Th><Th>Marketer</Th><Th right>Contacted</Th><Th right>Inspections</Th><Th right>Leads</Th><Th right>Deals</Th><Th right>Revenue</Th></tr>
                </thead>
                <tbody>
                  {daily_reports.map((r) => (
                    <tr key={r.id} className="border-t border-black/5">
                      <Td className="font-mono text-xs">{r.report_date}</Td>
                      <Td className="font-bold">{r.marketer_name}</Td>
                      <Td right>{r.customers_contacted}</Td>
                      <Td right>{r.inspections_done}</Td>
                      <Td right>{r.leads_generated}</Td>
                      <Td right className="font-bold text-blue-700">{r.closed_deals}</Td>
                      <Td right className="font-bold text-emerald-700">{ngn(r.revenue_generated)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ lbl, v }) {
  return (
    <div className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-xl p-3 min-w-[100px]">
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{lbl}</div>
      <div className="font-display font-extrabold text-lg mt-0.5 tabular-nums">{v}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-black/8 mb-6 overflow-hidden">
      <div className="px-5 py-3 border-b border-black/5 font-display font-extrabold text-base">{title}</div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}
function Empty({ msg }) { return <div className="p-10 text-center text-sm text-black/45">{msg}</div>; }
function Th({ children, right }) { return <th className={`px-4 py-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, className = "" }) { return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>; }
